import { init, FeatureFlagMapping } from '@goldcast/feature-flags';
import { getEnvConfig } from '@/constants';
import featureFlagStore from '@/stores/featureFlagStore';

// Define the mapping of feature flag keys to their value types
export type FeatureFlagTypeMap = {
  Use_CL_AI_Debug_Tools: boolean;
  Use_CL_Fast_Accurate: boolean;
  Use_CL_Show_Player_Frames: boolean;
  Use_CL_Init_New_Player_Config: boolean;
  Use_CL_Test_RS_Date: string;
  Use_CL_Uploads_In_VL: boolean;
  Use_CL_New_Recording_Studio_Timeline: boolean;
  USE_CL_FILLER_WORDS: boolean;
  Use_CL_Filler_Words_Menu: boolean;
  Use_CL_AI_Edits_Beta: boolean;
  Use_CL_Copy_Clip_To_Test_AiEdits: boolean;
  USE_CL_MERGE_TRANSCRIPT_FOR_RS: boolean;
  Use_CL_New_HomePage: boolean;
  Use_CL_Timeline_Add_Menu: boolean;
  Use_CL_Multi_Word_Edit_Transcript_No_Srt_Check: boolean;
  Use_CL_Face_Rec_To_Default_Position: boolean;
  Use_CL_Animated_Speaker_Cards: boolean;
  Use_CL_New_AI_Search: boolean;
  Use_CL_workspace_filters: boolean;
  Use_CL_Replace_Audio: boolean;
  Use_CL_Replace_Video_Overlays: boolean;
  Use_CL_Show_Person_and_Face_Coordinates_in_Manual_Reposition: boolean;
  Use_CL_Splits_In_Timeline: boolean;
  Use_CL_Overlay_Layering: boolean;
  Use_CL_AI_Edits_Auto_Open: boolean;
  Use_CL_Remotion_Thumbnail: boolean;
  Use_CL_Screenshare_Detection: boolean;
  Use_CL_Audio_Transitions: boolean;
  Use_CL_Segment_Transitions: boolean;
  Use_CL_New_Video_Tag_In_Preview: boolean;
  Use_CL_Delete_Config: boolean;
  Use_CL_AI_Edits_With_Splits: boolean;
  Use_CL_Fit_Mode_SS_In_Uploads: boolean;
  Use_CL_Browserless_Captions: boolean;
  Use_CL_Platform_Brandkit: boolean;
};

export enum FeatureFlagKeys {
  /* PERMANENT FLAGS */
  Use_CL_AI_Debug_Tools = 'Use_CL_AI_Debug_Tools',
  /* TEMPORARY FLAGS */
  Use_CL_Fast_Accurate = 'Use_CL_Fast_Accurate',
  Use_CL_Show_Player_Frames = 'Use_CL_Show_Player_Frames',
  Use_CL_Init_New_Player_Config = 'Use_CL_Init_New_Player_Config',
  Use_CL_Test_RS_Date = 'Use_CL_Test_RS_Date',
  Use_CL_Uploads_In_VL = 'Use_CL_Uploads_In_VL',
  Use_CL_New_Recording_Studio_Timeline = 'Use_CL_New_Recording_Studio_Timeline',
  USE_CL_FILLER_WORDS = 'USE_CL_FILLER_WORDS',
  Use_CL_Filler_Words_Menu = 'Use_CL_Filler_Words_Menu',
  Use_CL_AI_Edits_Beta = 'Use_CL_AI_Edits_Beta',
  Use_CL_Copy_Clip_To_Test_AiEdits = 'Use_CL_Copy_Clip_To_Test_AiEdits',
  USE_CL_MERGE_TRANSCRIPT_FOR_RS = 'USE_CL_MERGE_TRANSCRIPT_FOR_RS',
  Use_CL_New_HomePage = 'Use_CL_New_HomePage',
  Use_CL_Timeline_Add_Menu = 'Use_CL_Timeline_Add_Menu',
  Use_CL_Multi_Word_Edit_Transcript_No_Srt_Check = 'Use_CL_Multi_Word_Edit_Transcript_No_Srt_Check',
  Use_CL_Face_Rec_To_Default_Position = 'Use_CL_Face_Rec_To_Default_Position',
  Use_CL_Animated_Speaker_Cards = 'Use_CL_Animated_Speaker_Cards',
  Use_CL_New_AI_Search = 'Use_CL_New_AI_Search',
  Use_CL_workspace_filters = 'Use_CL_workspace_filters',
  Use_CL_Replace_Audio = 'Use_CL_Replace_Audio',
  Use_CL_Replace_Video_Overlays = 'Use_CL_Replace_Video_Overlays',
  Use_CL_Show_Person_and_Face_Coordinates_in_Manual_Reposition = 'Use_CL_Show_Person_and_Face_Coordinates_in_Manual_Reposition',
  Use_CL_Splits_In_Timeline = 'Use_CL_Splits_In_Timeline',
  Use_CL_Overlay_Layering = 'Use_CL_Overlay_Layering',
  Use_CL_AI_Edits_Auto_Open = 'Use_CL_AI_Edits_Auto_Open',
  Use_CL_Remotion_Thumbnail = 'Use_CL_Remotion_Thumbnail',
  Use_CL_Screenshare_Detection = 'Use_CL_Screenshare_Detection',
  Use_CL_Audio_Transitions = 'Use_CL_Audio_Transitions',
  Use_CL_Segment_Transitions = 'Use_CL_Segment_Transitions',
  Use_CL_New_Video_Tag_In_Preview = 'Use_CL_New_Video_Tag_In_Preview',
  Use_CL_Fit_Mode_SS_In_Uploads = 'Use_CL_Fit_Mode_SS_In_Uploads',
  Use_CL_Delete_Config = 'Use_CL_Delete_Config',
  Use_CL_AI_Edits_With_Splits = 'Use_CL_AI_Edits_With_Splits',
  Use_CL_Browserless_Captions = 'Use_CL_Browserless_Captions',
  Use_CL_Platform_Brandkit = 'Use_CL_Platform_Brandkit'
}

export const handleFlagValueChange =
  <T extends keyof FeatureFlagTypeMap>(flag: T) =>
  (value: FeatureFlagTypeMap[T]) => {
    featureFlagStore.update(data => ({
      ...data,
      [flag]: value
    }));
  };

export const featureFlags = (): FeatureFlagMapping<FeatureFlagTypeMap> => ({
  [FeatureFlagKeys.Use_CL_AI_Debug_Tools]: {
    value: false,
    changeHandler: handleFlagValueChange(FeatureFlagKeys.Use_CL_AI_Debug_Tools)
  },
  [FeatureFlagKeys.Use_CL_Fast_Accurate]: {
    value: false,
    changeHandler: handleFlagValueChange(FeatureFlagKeys.Use_CL_Fast_Accurate)
  },
  [FeatureFlagKeys.Use_CL_Show_Player_Frames]: {
    value: false,
    changeHandler: handleFlagValueChange(FeatureFlagKeys.Use_CL_Show_Player_Frames)
  },
  [FeatureFlagKeys.Use_CL_Init_New_Player_Config]: {
    value: false,
    changeHandler: handleFlagValueChange(FeatureFlagKeys.Use_CL_Init_New_Player_Config)
  },
  [FeatureFlagKeys.Use_CL_Test_RS_Date]: {
    value: '',
    changeHandler: handleFlagValueChange(FeatureFlagKeys.Use_CL_Test_RS_Date)
  },
  [FeatureFlagKeys.Use_CL_Uploads_In_VL]: {
    value: false,
    changeHandler: handleFlagValueChange(FeatureFlagKeys.Use_CL_Uploads_In_VL)
  },
  [FeatureFlagKeys.Use_CL_New_Recording_Studio_Timeline]: {
    value: false,
    changeHandler: handleFlagValueChange(FeatureFlagKeys.Use_CL_New_Recording_Studio_Timeline)
  },
  [FeatureFlagKeys.USE_CL_FILLER_WORDS]: {
    value: false,
    changeHandler: handleFlagValueChange(FeatureFlagKeys.USE_CL_FILLER_WORDS)
  },
  [FeatureFlagKeys.Use_CL_Filler_Words_Menu]: {
    value: false,
    changeHandler: handleFlagValueChange(FeatureFlagKeys.Use_CL_Filler_Words_Menu)
  },
  [FeatureFlagKeys.Use_CL_AI_Edits_Beta]: {
    value: false,
    changeHandler: handleFlagValueChange(FeatureFlagKeys.Use_CL_AI_Edits_Beta)
  },
  [FeatureFlagKeys.Use_CL_Copy_Clip_To_Test_AiEdits]: {
    value: false,
    changeHandler: handleFlagValueChange(FeatureFlagKeys.Use_CL_Copy_Clip_To_Test_AiEdits)
  },
  [FeatureFlagKeys.USE_CL_MERGE_TRANSCRIPT_FOR_RS]: {
    value: false,
    changeHandler: handleFlagValueChange(FeatureFlagKeys.USE_CL_MERGE_TRANSCRIPT_FOR_RS)
  },
  [FeatureFlagKeys.Use_CL_New_HomePage]: {
    value: false,
    changeHandler: handleFlagValueChange(FeatureFlagKeys.Use_CL_New_HomePage)
  },
  [FeatureFlagKeys.Use_CL_Timeline_Add_Menu]: {
    value: false,
    changeHandler: handleFlagValueChange(FeatureFlagKeys.Use_CL_Timeline_Add_Menu)
  },
  [FeatureFlagKeys.Use_CL_Multi_Word_Edit_Transcript_No_Srt_Check]: {
    value: false,
    changeHandler: handleFlagValueChange(FeatureFlagKeys.Use_CL_Multi_Word_Edit_Transcript_No_Srt_Check)
  },
  [FeatureFlagKeys.Use_CL_Face_Rec_To_Default_Position]: {
    value: false,
    changeHandler: handleFlagValueChange(FeatureFlagKeys.Use_CL_Face_Rec_To_Default_Position)
  },
  [FeatureFlagKeys.Use_CL_Animated_Speaker_Cards]: {
    value: false,
    changeHandler: handleFlagValueChange(FeatureFlagKeys.Use_CL_Animated_Speaker_Cards)
  },
  [FeatureFlagKeys.Use_CL_New_AI_Search]: {
    value: false,
    changeHandler: handleFlagValueChange(FeatureFlagKeys.Use_CL_New_AI_Search)
  },
  [FeatureFlagKeys.Use_CL_workspace_filters]: {
    value: false,
    changeHandler: handleFlagValueChange(FeatureFlagKeys.Use_CL_workspace_filters)
  },
  [FeatureFlagKeys.Use_CL_Replace_Audio]: {
    value: false,
    changeHandler: handleFlagValueChange(FeatureFlagKeys.Use_CL_Replace_Audio)
  },
  [FeatureFlagKeys.Use_CL_Replace_Video_Overlays]: {
    value: false,
    changeHandler: handleFlagValueChange(FeatureFlagKeys.Use_CL_Replace_Video_Overlays)
  },
  [FeatureFlagKeys.Use_CL_Show_Person_and_Face_Coordinates_in_Manual_Reposition]: {
    value: false,
    changeHandler: handleFlagValueChange(FeatureFlagKeys.Use_CL_Show_Person_and_Face_Coordinates_in_Manual_Reposition)
  },
  [FeatureFlagKeys.Use_CL_Splits_In_Timeline]: {
    value: false,
    changeHandler: handleFlagValueChange(FeatureFlagKeys.Use_CL_Splits_In_Timeline)
  },
  [FeatureFlagKeys.Use_CL_Overlay_Layering]: {
    value: false,
    changeHandler: handleFlagValueChange(FeatureFlagKeys.Use_CL_Overlay_Layering)
  },
  [FeatureFlagKeys.Use_CL_AI_Edits_Auto_Open]: {
    value: false,
    changeHandler: handleFlagValueChange(FeatureFlagKeys.Use_CL_AI_Edits_Auto_Open)
  },
  [FeatureFlagKeys.Use_CL_Remotion_Thumbnail]: {
    value: false,
    changeHandler: handleFlagValueChange(FeatureFlagKeys.Use_CL_Remotion_Thumbnail)
  },
  [FeatureFlagKeys.Use_CL_Screenshare_Detection]: {
    value: false,
    changeHandler: handleFlagValueChange(FeatureFlagKeys.Use_CL_Screenshare_Detection)
  },
  [FeatureFlagKeys.Use_CL_Audio_Transitions]: {
    value: false,
    changeHandler: handleFlagValueChange(FeatureFlagKeys.Use_CL_Audio_Transitions)
  },
  [FeatureFlagKeys.Use_CL_Segment_Transitions]: {
    value: false,
    changeHandler: handleFlagValueChange(FeatureFlagKeys.Use_CL_Segment_Transitions)
  },
  [FeatureFlagKeys.Use_CL_New_Video_Tag_In_Preview]: {
    value: false,
    changeHandler: handleFlagValueChange(FeatureFlagKeys.Use_CL_New_Video_Tag_In_Preview)
  },
  [FeatureFlagKeys.Use_CL_Delete_Config]: {
    value: false,
    changeHandler: handleFlagValueChange(FeatureFlagKeys.Use_CL_Delete_Config)
  },
  [FeatureFlagKeys.Use_CL_AI_Edits_With_Splits]: {
    value: false,
    changeHandler: handleFlagValueChange(FeatureFlagKeys.Use_CL_AI_Edits_With_Splits)
  },
  [FeatureFlagKeys.Use_CL_Fit_Mode_SS_In_Uploads]: {
    value: false,
    changeHandler: handleFlagValueChange(FeatureFlagKeys.Use_CL_Fit_Mode_SS_In_Uploads)
  },
  [FeatureFlagKeys.Use_CL_Browserless_Captions]: {
    value: false,
    changeHandler: handleFlagValueChange(FeatureFlagKeys.Use_CL_Browserless_Captions)
  },
  [FeatureFlagKeys.Use_CL_Platform_Brandkit]: {
    value: false,
    changeHandler: handleFlagValueChange(FeatureFlagKeys.Use_CL_Platform_Brandkit)
  }
});

// store promise in var for cases of calling this function from multiple places
let initializationPromise: Promise<void> | null = null;

export const initializeFeatureFlags = user => {
  if (featureFlagStore.getSnapshot().areFlagsLoaded) {
    return Promise.resolve();
  }
  if (!initializationPromise) {
    initializationPromise = new Promise<void>((resolve, reject) => {
      init<FeatureFlagTypeMap>(
        getEnvConfig('APP_HARNESS_SECRET') || '',
        {
          identifier: user.id || 'anonymous',
          name: `${user.first_name} ${user.last_name}` || 'anonymous',
          attributes: {
            role: 2, // admin role
            event: '',
            eventOrganization: user.organization,
            is_content_lab_standalone: user.is_content_lab_standalone,
            appHostname: window.location?.hostname
          }
        },
        featureFlags(),
        {
          ready: () => {
            featureFlagStore.update(data => ({ ...data, areFlagsLoaded: true }));
            return resolve();
          },
          errored: () => {
            featureFlagStore.update(data => ({ ...data, areFlagsLoaded: true }));
            return reject();
          },
          errorAuth: () => {
            featureFlagStore.update(data => ({ ...data, areFlagsLoaded: true }));
            return reject();
          }
        }
      );
    });
  }
  return initializationPromise;
};
